import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bell } from "lucide-react";

import type { GongChannel } from "../../application/useGongChannel";
import { formatGongCountdown } from "../../domain/meetingGong";

/**
 * How long until the next gong, in the corner of the room.
 *
 * A sequence is otherwise invisible: the room goes quiet and nobody — the
 * therapist included — knows whether the next sound is in one minute or in
 * nine, which is exactly the arithmetic the gong exists to take away. Shown
 * to the patient too, since they are the one sitting in the silence.
 *
 * It ticks here rather than in the channel so that one second passing
 * re-renders a badge instead of the whole call.
 *
 * `role="timer"` with no live region: a countdown announced every second
 * would talk over everything else a screen reader has to say, and the number
 * is read on demand like any other text.
 */
export default function MeetingGongCountdown({
    channel,
}: {
    channel: GongChannel;
}) {
    const { t } = useTranslation();
    const { nextAt } = channel;
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (nextAt === null) return;
        setNow(Date.now());
        const tick = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(tick);
    }, [nextAt]);

    if (nextAt === null) return null;

    return (
        <div
            className="pointer-events-none absolute right-3 top-3 z-10"
            data-testid="gong-countdown"
        >
            <p
                role="timer"
                aria-live="off"
                className="flex items-center gap-2 rounded-full border border-room-line bg-room-base/85 px-3.5 py-1.5 text-xs font-medium text-room-ink-muted backdrop-blur-sm"
            >
                <Bell size={13} aria-hidden="true" />
                <span className="sr-only">{t("meetingGong.next")}</span>
                <span className="font-semibold tabular-nums text-room-ink">
                    {formatGongCountdown(nextAt - now)}
                </span>
            </p>
        </div>
    );
}
